import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import { 
    ProfileType, 
    ProfileResponse, 
    emptyProfile, 
    DEFAULT_PROFILE_IMAGE,
    UpdateUserProfileParams,
    DeleteUserProfileParams
} from './types';

/**
 * Update user profile via API
 * @param params UpdateUserProfileParams object with profile fields to update
 * @returns Promise with updated profile or error
 */
export const updateProfileAPI = async (params: UpdateUserProfileParams): Promise<ProfileType | Error> => {
    try {
        const res = await api.patch<ProfileResponse<ProfileType>>('/user/profile', 
            params, 
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`
                }
            }
        );

        if (res.data?.code === 200) {
            return res.data.data;
        } 
        
        return new Error(res.data?.message || 'Failed to update profile');
    } catch (e) {
        console.error('Profile update failed via API:', e);
        return new Error('Failed to update profile');
    }
};

/**
 * Get user profile by ID via API
 * @param targetid User ID
 * @returns Promise with user profile
 */
export const readProfileAPI = async (targetid: string): Promise<ProfileType | typeof emptyProfile> => {
    try {
        const res = await readProfileListAPI([targetid]);
        
        if (res && res.length > 0) {
            const profile = res[0];
            return {
                ...profile,
                image: profile.image || DEFAULT_PROFILE_IMAGE
            };
        } 
        return emptyProfile;
    } catch (e) {
        console.error('Failed to read profile via API:', e);
        return emptyProfile;
    }
};

/**
 * Get user profile by email via API
 * @param email User email
 * @returns Promise with user profile
 */
export const readProfileByEmailAPI = async (email: string): Promise<ProfileType | typeof emptyProfile> => {
    try {
        const res = await readProfileListAPI([email]);
        
        if (res && res.length > 0) {
            const profile = res[0];
            return {
                ...profile,
                image: profile.image || DEFAULT_PROFILE_IMAGE
            };
        }
        return emptyProfile;
    } catch (e) {
        console.error('Failed to read profile by email via API:', e);
        return emptyProfile;
    }
};

/**
 * Get multiple user profiles by ID via API
 * @param targetids Array of user IDs or emails
 * @returns Promise with array of profiles or null
 */
export const readProfileListAPI = async (targetids: string[]): Promise<ProfileType[] | null> => {
    try {
        // Ensure we have user IDs to look up
        if (!targetids.length) {
            return null;
        }
        
        const res = await api.post<ProfileResponse<ProfileType[]>>(
            '/user/profile', 
            { targetid: targetids.join(',') }, 
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`
                }
            }
        );
        
        if (res.data?.code === 200) {
            return res.data.data;
        }
        
        return null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 404) {
            // If not found, return null. its not error
            return null;
        }
        console.error('Failed to read profile list via API:', e);
        return null;
    }
};

/**
 * Delete a user profile via API
 * @param params DeleteUserProfileParams object with userid to delete
 * @returns Promise<boolean> indicating success or failure
 */
export const removeProfileAPI = async (params: DeleteUserProfileParams): Promise<boolean> => {
    try {
        const res = await api.delete<ProfileResponse<any>>(
            '/user/profile', 
            {
                params,
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`
                }
            }
        );
        
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to remove profile via API:', e);
        return false;
    }
};

// Export types
export type { ProfileType } from './types';